import { useEffect, useState } from 'react';
import { useSpecialist } from 'src/contexts/SpecialistContext';
import ChatSearchInput from 'src/features/chat/components/ChatSearchInput';
import SpecialistsModal from 'src/features/chat/components/SpecialistsModal';
import useChatUrlState from 'src/features/chat/hooks/useChatUrlState';
import type { ChatRequest } from 'src/features/chat/hooks/useChatStream';
import { stashHomeChatHandoff, type HomeChatHandoff } from 'src/features/chat/pendingChatRequest';
import { useIsFreemiumUser } from 'src/hooks/useIsFreemiumUser';

function ModalBridge({ onOpen, onOpenById }: { onOpen: () => void; onOpenById: (specialistId: string) => void }) {
  const { setOpenManageModal, setOpenSpecialistById } = useSpecialist();

  useEffect(() => {
    setOpenManageModal(() => onOpen);
  }, [onOpen, setOpenManageModal]);

  useEffect(() => {
    setOpenSpecialistById(() => onOpenById);
  }, [onOpenById, setOpenSpecialistById]);

  return null;
}

const GUEST_CHAT_DISABLED_TITLE = 'Chat is not available for guest accounts';

export default function HomeChatComposer({
  onSubmit,
  prefill,
  autoFocus = false,
  disabled = false,
  disabledReason
}: {
  onSubmit: (request: ChatRequest) => void;
  prefill?: { id: number; text: string } | null;
  autoFocus?: boolean;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const isFreemiumUser = useIsFreemiumUser();
  const { chatFilters, setChatFilters, filtersReady, specialistId, googleSearchPermission, setGoogleSearchPermission } =
    useChatUrlState();
  const [specialistsModalOpen, setSpecialistsModalOpen] = useState(false);
  const [initialSpecialistId, setInitialSpecialistId] = useState<string | null>(null);

  return (
    <div
      className={disabled ? 'pointer-events-none opacity-60' : undefined}
      aria-disabled={disabled}
      title={disabled ? (disabledReason ?? GUEST_CHAT_DISABLED_TITLE) : undefined}
    >
      {!isFreemiumUser && (
        <ModalBridge
          onOpen={() => setSpecialistsModalOpen(true)}
          onOpenById={(id: string) => {
            setInitialSpecialistId(id);
            setSpecialistsModalOpen(true);
          }}
        />
      )}
      <ChatSearchInput
        handleResponse={disabled ? () => undefined : onSubmit}
        onLeavePageSubmit={
          disabled
            ? undefined
            : (handoff: HomeChatHandoff) => {
                stashHomeChatHandoff(handoff);
                const text = handoff.pendingParts
                  .flatMap((part) => (part.type === 'basic' ? [part.content] : []))
                  .join('');
                onSubmit({
                  parts: text ? [{ type: 'text', text }] : [],
                  specialist_id: specialistId ?? undefined,
                  google_search: googleSearchPermission
                });
              }
        }
        allowFilters
        chatSearchValue=""
        prefill={prefill}
        chatFilters={chatFilters}
        setChatFilters={setChatFilters}
        filtersReady={filtersReady}
        googleSearchPermission={googleSearchPermission}
        setGoogleSearchPermission={setGoogleSearchPermission}
        enableFileUpload
        showPageFileDropOverlay
        enableSpecialist={!isFreemiumUser}
        editorClassName="min-h-16"
        autoFocus={!disabled && autoFocus}
        disabled={disabled}
      />
      {!isFreemiumUser && (
        <SpecialistsModal
          open={specialistsModalOpen}
          onClose={() => {
            setSpecialistsModalOpen(false);
            setInitialSpecialistId(null);
          }}
          initialSpecialistId={initialSpecialistId}
        />
      )}
    </div>
  );
}
